import dayjs from 'dayjs';
import { useMemo } from 'react';
import {
  ClaimRecord,
  GetClaimsForOrderLineId,
  GetRefundsForOrderLineId,
  RefundRecord
} from '../../../lib/cache/ExtendedOrderCache';
import ItemRefundAndClaimTagsView from './ItemRefundAndClaimTagsView';
import { SearchObject } from '~/components/search/algolia/Models';

interface props {
  hit?: SearchObject;
  orderlineId: number | string | undefined;
  wrapper: (x: React.ReactNode) => React.ReactNode;
}

export default function ItemRefundAndClaimTags(props: props) {
  const refunds: RefundRecord[] = useMemo(() => {
    if (!props.orderlineId) {
      return [];
    }

    const cached = GetRefundsForOrderLineId(props.orderlineId);
    if (cached.length > 0) {
      return cached;
    }

    if (props.hit?.refunded) {
      return [
        {
          orderlineId: props.orderlineId.toString(),
          amount: props.hit.refundAmount ?? 0,
          status: 'Refunded',
          created: (() => {
            const ts = props.hit.refundDate;
            if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0) {
              return undefined;
            }
            const d = dayjs.unix(ts);
            return d.isValid() ? d.toDate() : undefined;
          })()
        }
      ];
    }

    return [];
  }, [props.orderlineId, props.hit]);

  const claims: ClaimRecord[] = useMemo(() => {
    if (!props.orderlineId) {
      return [];
    }

    const cached = GetClaimsForOrderLineId(props.orderlineId);
    if (cached.length > 0) {
      return cached;
    }

    if (props.hit?.hasClaim) {
      return [
        {
          orderlineId: props.orderlineId.toString(),
          status: props.hit.claimStatus,
          id: undefined,
          created: (() => {
            if (isNaN(props.hit.claimDate) || props.hit.claimDate <= 0) {
              return undefined;
            }
            const d = dayjs.unix(props.hit.claimDate);
            return d.isValid() ? d.toDate() : undefined;
          })()
        }
      ];
    }

    return [];
  }, [props.orderlineId, props.hit]);

  if (!props.orderlineId || (refunds.length === 0 && claims.length === 0)) {
    return null;
  }

  return props.wrapper(
    <ItemRefundAndClaimTagsView claims={claims} refunds={refunds} orderlineId={props.orderlineId} />
  );
}
